import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Spin } from 'antd';
import ModalItem from './ModalItem';
import round2Decimals from './round2Decimals';

// Modal de solo lectura con los insumos que se eligieron para un renglon de kit variable
// (insumo compuesto con kitVariable). La seleccion vive en `insumos[].componentesKit` de la
// Venta o del Pedido y ahi solo se guarda `{insumoId, cantidad, descripcion}`; la clave y la
// unidad se resuelven aqui contra el catalogo, con el mismo endpoint que usa el modal de
// captura (ElegirComponentesKit).
//
// Hermano estructural de DisponibilidadInsumoModal / HistorialInsumoClienteModal: mismo
// contrato de props (headersToken y empresaId por prop, no por connect), misma forma de
// cargar al abrir y limpiar al cerrar, y los mismos estilos de tabla.
const ComponentesKitModal = ({
  visible,
  onClose,
  componentes,
  cantidadLinea,
  nombreInsumo,
  headersToken,
  empresaId,
}) => {
  const [loading, setLoading] = useState(false);
  // insumoId (string) -> insumo del catalogo. Puede quedar vacio: la tabla igual se pinta
  // con la descripcion que quedo denormalizada en el documento.
  const [insumosPorId, setInsumosPorId] = useState({});

  useEffect(() => {
    if (visible) {
      consultarInsumos();
    } else {
      setInsumosPorId({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const consultarInsumos = async () => {
    const insumoIds = (componentes || [])
      .filter((valueComponente) => valueComponente && valueComponente.insumoId)
      .map((valueComponente) => valueComponente.insumoId);

    if (insumoIds.length === 0) {
      setInsumosPorId({});
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        'insumos/selectInsumosPorIds',
        { empresaId, insumoIds },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        const mapa = {};
        (res.data.insumos || []).forEach((valueInsumo) => {
          mapa[valueInsumo._id.toString()] = valueInsumo;
        });
        setInsumosPorId(mapa);
      } else {
        setInsumosPorId({});
      }
    } catch {
      // Un insumo borrado, o el endpoint caido, no debe dejar el modal vacio: se cae a la
      // descripcion guardada en el renglon y las columnas de catalogo quedan en "-".
      setInsumosPorId({});
    } finally {
      setLoading(false);
    }
  };

  const thStyle = {
    padding: '6px 10px',
    background: '#f5f5f5',
    fontWeight: 600,
    fontSize: 11,
    color: '#374151',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
  };

  const tdStyle = (align = 'left') => ({
    padding: '5px 10px',
    fontSize: 12,
    color: '#111827',
    borderBottom: '1px solid #f3f4f6',
    textAlign: align,
  });

  const sinDatos = (cols) => (
    <tr>
      <td colSpan={cols} style={{ textAlign: 'center', padding: '10px', color: '#9ca3af', fontSize: 12 }}>
        Sin registros
      </td>
    </tr>
  );

  const cantidadDelRenglon = Number(cantidadLinea) || 0;

  const columnas = [
    { label: 'Clave' },
    { label: 'Descripción' },
    { label: 'Unidad' },
    { label: 'Cant. unitaria', align: 'right' },
    { label: 'Cant. total', align: 'right' },
  ];

  const filas = (componentes || []).map((valueComponente, i) => {
    const insumo = valueComponente?.insumoId
      ? insumosPorId[valueComponente.insumoId.toString()]
      : undefined;
    const cantidad = Number(valueComponente?.cantidad) || 0;
    return (
      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
        <td style={tdStyle()}>{insumo?.clave || '-'}</td>
        <td style={tdStyle()}>{insumo?.descripcion || valueComponente?.descripcion || '-'}</td>
        <td style={tdStyle()}>{insumo?.unidad || '-'}</td>
        <td style={tdStyle('right')}>{cantidad}</td>
        <td style={tdStyle('right')}>{round2Decimals(cantidad * cantidadDelRenglon)}</td>
      </tr>
    );
  });

  return (
    <ModalItem
      title={`Insumos del kit — ${nombreInsumo || ''}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      spinning={false}
    >
      <Spin spinning={loading}>
        <div
          style={{
            background: '#00BFD615',
            border: '1px solid #00BFD6',
            borderRadius: 8,
            padding: '8px 14px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 12, color: '#005f6b' }}>Cantidad del renglón:</span>
          <strong style={{ fontSize: 13, color: '#005f6b' }}>{cantidadDelRenglon}</strong>
          <span style={{ marginLeft: 16, fontSize: 12, color: '#374151' }}>
            La columna <strong>Cant. total</strong> ya está multiplicada por esta cantidad.
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #00BFD625' }}>
          <thead>
            <tr>
              {columnas.map((c, i) => (
                <th key={i} style={{ ...thStyle, textAlign: c.align || 'left' }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{filas.length > 0 ? filas : sinDatos(columnas.length)}</tbody>
        </table>
      </Spin>
    </ModalItem>
  );
};

export default ComponentesKitModal;
